import { useEffect, useMemo, useRef, useState } from 'react';
import cn from 'classnames';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBalanceScale, faBan, faDollarSign, faSearch, faSignalAlt3, faStar, faSyncAlt } from '@fortawesome/pro-light-svg-icons';

import { searchScriptConfigs } from '../../helpers/script_runner_helpers';

const SCRIPT_ICON_KEYWORDS = [
  { iconProps: { icon: faBan }, keywords: ['ban'] },
  { iconProps: { icon: faDollarSign }, keywords: ['commission', 'payout', 'billing'] },
  { iconProps: { icon: faBalanceScale }, keywords: ['legal'] },
  { iconProps: { icon: faSyncAlt }, keywords: ['sync'] },
  // The signal glyph fills only the bottom-left of its canvas, so grow and shift it to sit centered.
  { iconProps: { icon: faSignalAlt3, transform: 'grow-4 right-3 up-2.5' }, keywords: ['stats', 'analytics'] }
];
const getScriptIconProps = config => {
  const searchable = `${config.name || ''} ${config.scriptPath || ''}`.toLowerCase();
  return SCRIPT_ICON_KEYWORDS.find(({ keywords }) => keywords.some(keyword => searchable.includes(keyword)))?.iconProps || { icon: faStar };
};

const ScriptRunnerSelection = ({ configs, isLoadingConfigs, configError, onSelectConfig }) => {
  const [query, setQuery] = useState('');
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(0);
  const searchInputRef = useRef(null);

  const hasSearch = configs.length > 3;
  const cardConfigs = hasSearch ? configs.slice(0, 3) : configs;
  const searchResults = useMemo(() => searchScriptConfigs(configs, query), [configs, query]);

  useEffect(() => {
    if (!hasSearch) return undefined;
    const onKeyDown = e => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        searchInputRef.current?.focus();
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [hasSearch]);

  const handleQueryChange = e => {
    setQuery(e.target.value);
    setHighlightedIndex(0);
    setIsDropdownOpen(true);
  };

  const handleSearchKeyDown = e => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlightedIndex(index => Math.min(index + 1, searchResults.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlightedIndex(index => Math.max(index - 1, 0));
    } else if (e.key === 'Enter' && searchResults[highlightedIndex]) {
      e.preventDefault();
      onSelectConfig(searchResults[highlightedIndex].id);
    } else if (e.key === 'Escape') {
      setIsDropdownOpen(false);
      searchInputRef.current?.blur();
    }
  };

  if (isLoadingConfigs) {
    return (
      <div className='script-runner-selection'>
        <p className='script-runner-selection-quiet'>Loading scripts...</p>
      </div>
    );
  }

  return (
    <div className='script-runner-selection'>
      {configError && <div className='script-runner-alert script-runner-alert-error'>{configError}</div>}

      <div className='script-runner-selection-lockup'>
        <div className='script-runner-selection-copy'>
          <h1>{configs.length > 1 ? 'What do you need to run?' : 'Welcome to Script Runner'}</h1>
          {configs.length > 1 && <p>Search by name, path, or what it does.</p>}
        </div>

        {hasSearch && (
          <div className='script-runner-search'>
            <FontAwesomeIcon icon={faSearch} className='search-icon' />
            <input
              ref={searchInputRef}
              type='text'
              placeholder={`Search ${configs.length} Scripts`}
              value={query}
              onChange={handleQueryChange}
              onFocus={() => setIsDropdownOpen(true)}
              onBlur={() => setIsDropdownOpen(false)}
              onKeyDown={handleSearchKeyDown}
            />
            <span className='search-shortcut'>⌘ K</span>
            {isDropdownOpen && (
              <div className='script-runner-search-results'>
                {searchResults.length ? (
                  searchResults.map((config, index) => (
                    <button
                      type='button'
                      key={config.id}
                      className={cn('script-runner-search-result', { highlighted: index === highlightedIndex })}
                      onMouseDown={e => {
                        e.preventDefault();
                        onSelectConfig(config.id);
                      }}
                      onMouseEnter={() => setHighlightedIndex(index)}
                    >
                      <FontAwesomeIcon {...getScriptIconProps(config)} className='result-icon' fixedWidth />
                      <span className='result-name'>{config.name}</span>
                      <span className='result-path'>{config.scriptPath}</span>
                    </button>
                  ))
                ) : (
                  <div className='script-runner-search-empty'>No scripts match “{query}”</div>
                )}
              </div>
            )}
          </div>
        )}
      </div>

      {configs.length > 0 ? (
        <div className='script-runner-selection-panel'>
          {hasSearch && <div className='panel-label'>Suggested</div>}
          <div className='panel-cards'>
            {cardConfigs.map(config => (
              <button type='button' key={config.id} className='script-runner-selection-card' onClick={() => onSelectConfig(config.id)}>
                <span className='card-icon'>
                  <FontAwesomeIcon {...getScriptIconProps(config)} />
                </span>
                <span className='card-name'>{config.name}</span>
                <span className='card-description'>{config.description}</span>
                <span className='card-path'>{config.scriptPath}</span>
              </button>
            ))}
          </div>
        </div>
      ) : (
        <p className='script-runner-selection-quiet'>No scripts are available to you yet.</p>
      )}
    </div>
  );
};

export default ScriptRunnerSelection;
